import { type Dtos, SuretyType } from "@inrev/common";

export const SFAA_CONTRACT_OBLIGEES = ["Private", "Other Public", "Federal"] as const;
export type SfaaContractObligee = (typeof SFAA_CONTRACT_OBLIGEES)[number];

export const isSfaaContractObligee = (value: string | null): value is SfaaContractObligee =>
	SFAA_CONTRACT_OBLIGEES.some((ownerType) => ownerType === value);

export const matchesSfaaObligee = (
	obligeeType: string | null,
	suretyType: SuretyType,
	ownerType: SfaaContractObligee,
) =>
	suretyType === SuretyType.contract
		? obligeeType === ownerType
		: !isSfaaContractObligee(obligeeType);

export const isSfaaCodeForOtherOwner = (
	obligeeType: string | null,
	nextOwnerType: SfaaContractObligee,
) => isSfaaContractObligee(obligeeType) && obligeeType !== nextOwnerType;

const OWNER_LABELS: Record<SfaaContractObligee, string> = {
	Private: "Private 8xx",
	"Other Public": "Other Public 7xx",
	Federal: "Federal 6xx",
};

export const sfaaContractObligeeLabel = (ownerType: SfaaContractObligee) => OWNER_LABELS[ownerType];

type SfaaCode = Dtos.SfaaCode.Get.Response;

export const equivalentSfaaCode = (
	codes: readonly SfaaCode[],
	selected: SfaaCode,
	nextOwnerType: SfaaContractObligee,
) => {
	const digit = OWNER_LABELS[nextOwnerType].replace(/^\D+/, "").charAt(0);
	const nextCode = `${digit}${selected.code.slice(1)}`;
	return codes.find((row) => row.code === nextCode && row.obligeeType === nextOwnerType);
};

export const EMPTY_SFAA_CODES: SfaaCode[] = [];

export const uniqueSfaaCodesById = <T extends { id: string }>(codes: readonly T[]) => [
	...new Map(codes.map((code) => [code.id, code])).values(),
];

export const mergeSfaaCatalog = (
	sfaaCodes: readonly SfaaCode[] | undefined,
	recentlyUsed: readonly SfaaCode[],
) => uniqueSfaaCodesById([...(sfaaCodes ?? EMPTY_SFAA_CODES), ...recentlyUsed]);

export const isSfaaFieldLocked = (disabled?: boolean, readonly?: boolean, isLoading?: boolean) =>
	Boolean(disabled || readonly || isLoading);
